import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  CalendarCheckIcon,
  CalendarPlusIcon,
  MapPinIcon,
  VideoIcon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { portalBookingApi } from '@/api/booking'
import { ApiError } from '@/api/errors'
import type { PortalBooking } from '@/api/types'
import { PORTAL_BOOKINGS_KEY } from '@/components/booking/portal-booking-flow'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'
import { bookingIcs, clockIn, viewerTimeZone, dayIn } from '@/lib/booking'
import { formatDateOnly } from '@/lib/format'
import { flag, readSearch } from '@/lib/search-params'

export const Route = createFileRoute('/portal/reservations/$bookingId')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch({ /** Juste réservée : on le dit. */ confirmee: flag() }, search),
  component: PortalBookingPage,
})

/** Télécharger le rendez-vous en `.ics` : l'agenda du contact l'ajoute. */
function downloadIcs(booking: PortalBooking) {
  const url = URL.createObjectURL(
    new Blob([bookingIcs(booking)], { type: 'text/calendar;charset=utf-8' }),
  )
  const link = document.createElement('a')
  link.href = url
  link.download = 'rendez-vous.ics'
  document.body.append(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Un rendez-vous réservé (lot 32) — la confirmation, et le lien des courriels
 * et de la cloche : quand, avec qui, où ; « Ajouter à mon agenda » ;
 * « Annuler » tant qu'il est à venir.
 */
function PortalBookingPage() {
  const { bookingId } = Route.useParams()
  const { confirmee } = Route.useSearch()
  const queryClient = useQueryClient()
  const t = msg().portalScheduling.booking
  const [cancelling, setCancelling] = useState(false)
  // Pas de lecture unitaire : la liste complète le porte.
  const bookings = useQuery({
    queryKey: [...PORTAL_BOOKINGS_KEY, 'tous'],
    queryFn: () => portalBookingApi.list({ upcoming: false }),
  })
  const cancel = useMutation({
    mutationFn: () => portalBookingApi.cancel(bookingId),
    onSuccess: () => {
      setCancelling(false)
      void queryClient.invalidateQueries({ queryKey: PORTAL_BOOKINGS_KEY })
      toast.success(t.cancelDone)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.cancelFailed)
    },
  })

  if (bookings.isPending) return <Skeleton className="h-48 w-full rounded-xl" />
  const booking = bookings.data?.find((item) => item.id === bookingId)
  if (bookings.isError || !booking) {
    return (
      <div className="space-y-4">
        <FormError>
          {bookings.error instanceof ApiError ? bookings.error.message : t.notFound}
        </FormError>
        <Button asChild variant="outline">
          <Link to="/portal/rendez-vous">{t.back}</Link>
        </Button>
      </div>
    )
  }

  const zone = viewerTimeZone()
  const when = `${formatDateOnly(dayIn(booking.start, zone))}, ${clockIn(booking.start, zone)}–${clockIn(booking.end, zone)}`
  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow={
          <Button asChild variant="ghost" size="sm">
            <Link to="/portal/rendez-vous">
              <ArrowLeftIcon />
              {t.back}
            </Link>
          </Button>
        }
        title={booking.subject}
        description={t.withHost(booking.host.name)}
      />
      {confirmee && !booking.cancelled && (
        <p
          className="flex items-center gap-2 rounded-md border border-success/40 bg-success/5 px-3 py-2 text-sm"
          data-testid="reservation-confirmee"
        >
          <CalendarCheckIcon className="size-4 text-success" aria-hidden="true" />
          {t.confirmed}
        </p>
      )}
      <Frame>
        <dl className="space-y-2 text-sm" data-testid="reservation">
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">{t.when}</dt>
            <dd className="font-medium">{when}</dd>
            {booking.cancelled && <Badge variant="muted">{t.cancelled}</Badge>}
          </div>
          {booking.conference_url ? (
            <div className="flex flex-wrap items-center gap-x-2">
              <dt className="inline-flex items-center gap-1 text-muted-foreground">
                <VideoIcon className="size-3.5" aria-hidden="true" />
                {t.video}
              </dt>
              <dd>
                <a
                  href={booking.conference_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="break-all text-primary hover:underline"
                >
                  {booking.conference_url}
                </a>
              </dd>
            </div>
          ) : booking.location ? (
            <div className="flex flex-wrap items-center gap-x-2">
              <dt className="inline-flex items-center gap-1 text-muted-foreground">
                <MapPinIcon className="size-3.5" aria-hidden="true" />
                {t.place}
              </dt>
              <dd>{booking.location}</dd>
            </div>
          ) : null}
          {booking.project && (
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-muted-foreground">{t.project}</dt>
              <dd>{booking.project.name}</dd>
            </div>
          )}
        </dl>
        {!booking.cancelled && (
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                downloadIcs(booking)
              }}
            >
              <CalendarPlusIcon />
              {t.addToCalendar}
            </Button>
            {booking.can_cancel && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCancelling(true)
                }}
              >
                <XIcon />
                {t.cancel}
              </Button>
            )}
          </div>
        )}
      </Frame>
      <ConfirmDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t.cancelTitle}
        description={t.cancelDescription(booking.host.name)}
        confirmLabel={t.cancelConfirm}
        cancelLabel={t.keep}
        destructive
        pending={cancel.isPending}
        onConfirm={() => {
          cancel.mutate()
        }}
      />
    </div>
  )
}
